import { useEffect, useState, createContext, useContext } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { contentApi } from '../../services/api';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../common';

// ---------------------------------------------------------------------------
// Types (mirror backend ShippingMethodsConfig)
// ---------------------------------------------------------------------------

interface BiText { es: string; en: string }

interface ShippingMethod {
  id: string;
  name: BiText;
  description: BiText;
  price: number;
  free_threshold: number;
  estimated_days_min: number;
  estimated_days_max: number;
  active: boolean;
  regions: string[];
}

interface ShippingMethodsConfig {
  methods: ShippingMethod[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Lang = 'es' | 'en';
const LangContext = createContext<Lang>('es');

const uid = () => `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const emptyBi = (): BiText => ({ es: '', en: '' });
const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

function BiInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: BiText;
  onChange: (v: BiText) => void;
  placeholder?: { es?: string; en?: string };
}) {
  const lang = useContext(LangContext);
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input
        type="text"
        value={value[lang] || ''}
        onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        className={inputClass}
        placeholder={placeholder?.[lang] || ''}
      />
    </div>
  );
}

function BiTextarea({
  label,
  value,
  onChange,
  rows = 2,
}: {
  label: string;
  value: BiText;
  onChange: (v: BiText) => void;
  rows?: number;
}) {
  const lang = useContext(LangContext);
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <textarea
        value={value[lang] || ''}
        onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        rows={rows}
        className={inputClass}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Method card editor
// ---------------------------------------------------------------------------

function MethodEditor({
  method,
  onChange,
  onRemove,
}: {
  method: ShippingMethod;
  onChange: (m: ShippingMethod) => void;
  onRemove: () => void;
}) {
  const update = (partial: Partial<ShippingMethod>) => onChange({ ...method, ...partial });

  const regionsCsv = method.regions.join(', ');
  const updateRegions = (csv: string) => {
    const list = csv
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s.length === 2);
    update({ regions: list });
  };

  return (
    <div className="border border-gray-200 rounded-xl p-5 space-y-4 bg-gray-50/50">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={method.active}
              onChange={(e) => update({ active: e.target.checked })}
              className="rounded text-vida-600 focus:ring-vida-500"
            />
            <span className={`text-xs font-semibold uppercase tracking-wider ${method.active ? 'text-green-700' : 'text-gray-400'}`}>
              {method.active ? 'Activo' : 'Inactivo'}
            </span>
          </label>
          <code className="text-xs text-gray-500">id: {method.id}</code>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-red-400 hover:text-red-600"
          aria-label="Eliminar método"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>

      <BiInput
        label="Nombre / Name"
        value={method.name}
        onChange={(v) => update({ name: v })}
        placeholder={{ es: 'Envío estándar', en: 'Standard shipping' }}
      />
      <BiTextarea label="Descripción / Description" value={method.description} onChange={(v) => update({ description: v })} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>Precio (EUR)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={method.price}
            onChange={(e) => update({ price: parseFloat(e.target.value) || 0 })}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Envío gratis a partir de (EUR)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={method.free_threshold}
            onChange={(e) => update({ free_threshold: parseFloat(e.target.value) || 0 })}
            className={inputClass}
            placeholder="0 = sin umbral"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Días estimados (mín)</label>
          <input
            type="number"
            min="0"
            value={method.estimated_days_min}
            onChange={(e) => update({ estimated_days_min: parseInt(e.target.value) || 0 })}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Días estimados (máx)</label>
          <input
            type="number"
            min="0"
            value={method.estimated_days_max}
            onChange={(e) => update({ estimated_days_max: parseInt(e.target.value) || 0 })}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Regiones (códigos ISO-3166 alpha-2, separados por coma)</label>
        <input
          type="text"
          value={regionsCsv}
          onChange={(e) => updateRegions(e.target.value)}
          className={inputClass}
          placeholder="ES, FR, PT (vacío = todos los países)"
        />
        <p className="text-xs text-gray-500 mt-1">Vacío = disponible en todos los países.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main editor
// ---------------------------------------------------------------------------

export default function ShippingMethodsEditor() {
  const [config, setConfig] = useState<ShippingMethodsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lang, setLang] = useState<Lang>('es');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await contentApi.getShippingMethods();
      if (res.data?.data) setConfig(res.data.data);
    } catch (e) {
      console.error('Failed to load shipping methods', e);
      toast.error('Error al cargar los métodos de envío');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await contentApi.saveShippingMethods(config);
      setDirty(false);
      toast.success('Métodos de envío guardados');
    } catch (e) {
      console.error('Failed to save shipping methods', e);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateMethod = (idx: number, m: ShippingMethod) => {
    if (!config) return;
    const next = [...config.methods];
    next[idx] = m;
    setConfig({ methods: next });
    setDirty(true);
  };

  const removeMethod = (idx: number) => {
    if (!config) return;
    setConfig({ methods: config.methods.filter((_, i) => i !== idx) });
    setDirty(true);
  };

  const addMethod = () => {
    if (!config) return;
    setConfig({
      methods: [
        ...config.methods,
        {
          id: uid(),
          name: emptyBi(),
          description: emptyBi(),
          price: 0,
          free_threshold: 0,
          estimated_days_min: 1,
          estimated_days_max: 5,
          active: true,
          regions: [],
        },
      ],
    });
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!config) {
    return <div className="p-8 text-center text-gray-500">No se pudo cargar la configuración.</div>;
  }

  return (
    <LangContext.Provider value={lang}>
      <div className="space-y-4">
        {/* Save bar */}
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-3 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Métodos de envío</h2>
            <p className="text-sm text-gray-500">Configura las opciones de entrega que verá el cliente al hacer checkout</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex bg-gray-100 rounded-lg p-1" role="tablist" aria-label="Idioma">
              <button
                type="button"
                onClick={() => setLang('es')}
                aria-pressed={lang === 'es'}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${lang === 'es' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ES
              </button>
              <button
                type="button"
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${lang === 'en' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                EN
              </button>
            </div>
            {dirty && <span className="text-xs text-amber-600 font-medium">Cambios sin guardar</span>}
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <LoadingSpinner size="sm" />}
              Guardar
            </button>
          </div>
        </div>

        {/* Methods list */}
        <div className="space-y-4">
          {config.methods.map((m, idx) => (
            <MethodEditor key={m.id} method={m} onChange={(updated) => updateMethod(idx, updated)} onRemove={() => removeMethod(idx)} />
          ))}
          {config.methods.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500">
              No hay métodos de envío configurados. Añade al menos uno para que tus clientes puedan completar el checkout.
            </div>
          )}
          <button
            onClick={addMethod}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors"
          >
            <PlusIcon className="w-4 h-4" /> Añadir método
          </button>
        </div>

        {/* Bottom save button */}
        {dirty && (
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <LoadingSpinner size="sm" />}
              Guardar cambios
            </button>
          </div>
        )}
      </div>
    </LangContext.Provider>
  );
}
